/**************************************************************************
 * Created on Tue Aug 27 2024
 *
 *  # Honeywell Code Usage Policy
 *
 * **Honeywell Source Code Usage Policy Classification:** Highly Confidential
 *
 * [comment]: # (Options for above are "Inner Source", "General Application", "Highly Confidential", "Export Controlled")
 *
 * **This repository has multiple classifications:** Yes
 *
 * [comment]: # (Options for above are "Yes" when subfolders contain a separate policy,"No" when the whole repo has the same policy)
 *
 * **Honeywell confidential - For internal use only.
 *
 * Property of and copyright ownership by Honeywell International Inc. and/or its affiliates.
 *
 * Use of source code must follow all Honeywell policies including the [HCE Source Code Access Policy](http: //go.honeywell.com/hce-code-access-policy).
 *
 * External use or distribution prohibited without written approval per Honeywell policies**
 *
********************************************************************************/

import React, { useEffect, useRef } from "react";
import { createContext, useState } from "react";
import { getCookie, ID_TOKEN_KEY, removeCookie } from "../../../interceptors/constant";
import { useDssContext } from "../../../DSSContext";
import { getUserDetail, invalidateToken, saveUserLoginDetails } from "../../api/api";
import { IUserDetail } from "../../models/customer-model";
import { LOGOUT_SLO_URL } from "../../constant/constant";
import { notifyError } from "../../../ui/dls/dls-snackbar/dls-snackbar";
import DSSLoader from "../../../ui/building-blocks/atoms/dss-loader/dss-loader";


const AuthContext = createContext({});


export const AuthProvider = (props: { children: JSX.Element }) => {

    const { setUserDetail, setSelectedCustomer } = useDssContext();
    const authMarker = getCookie(ID_TOKEN_KEY) ?? '';
    const [auth, setAuth] = useState({ isAuthenticated: Boolean(authMarker) });
    const [isUserDetailLoading, setIsUserDetailLoading] = useState(false);
    const lastFetchedUidRef = useRef<string>('');

    const trackUserLoginDetails = (trackingUserId: string) => {
        saveUserLoginDetails(trackingUserId).catch(() => {
        });
    };
    
    useEffect(() => {
        const uid = (getCookie(ID_TOKEN_KEY) ?? '').toLowerCase();
        const isGuestRoute = window.location.pathname.startsWith('/guest');

        if (uid && uid === lastFetchedUidRef.current) {
            return;
        }
        
        if (uid && !isGuestRoute) {
            setIsUserDetailLoading(true);
            getUserDetail(uid).then((res: IUserDetail) => {
                lastFetchedUidRef.current = uid;
                const trackingUserId = String(res?.honeywellId || uid);
                trackUserLoginDetails(trackingUserId);
                setUserDetail(res);
                const storedSelectedCustomer = sessionStorage.getItem('selectedCustomer') || '';
                const singleCustomerRefSeq =
                    res?.customers?.length === 1 && res.customers[0]?.customerRefSeq
                        ? String(res.customers[0].customerRefSeq)
                        : '';
                setSelectedCustomer(storedSelectedCustomer || singleCustomerRefSeq || '');
            }).catch((err: Error) => {
                setIsUserDetailLoading(false);
                notifyError(err || "Your Honeywell Id is not associated to any Company/Customer, please contact Technical Support Team.");
                if ((err as Error & { status?: number }).status === 401) logout();
            }).finally(() => {
                setIsUserDetailLoading(false);
            })
        } else {
            setIsUserDetailLoading(false);
        }

    }, [auth.isAuthenticated])

    const logout = async () => {
        try {
            await invalidateToken();
        } catch (err) {
        } finally {
            removeCookie(ID_TOKEN_KEY);
            lastFetchedUidRef.current = '';
            localStorage.removeItem('currentuser');
            sessionStorage.removeItem('isGuestUser');
            sessionStorage.removeItem('broadcastDontShowAgainGeneral');
            sessionStorage.removeItem('broadcastDontShowAgainDatabase');
            sessionStorage.removeItem('accountSelectionHandledGeneral');
            setUserDetail(undefined as any);
            setSelectedCustomer('');
            setAuth({ isAuthenticated: false });
            window.location.href = `${ LOGOUT_SLO_URL }?TargetResource=${ window.location.origin }/prelogin`;
        }
    };
    
    return (
        <AuthContext.Provider value={({ auth, setAuth, logout })}>
            {isUserDetailLoading ? <DSSLoader id="auth-user-detail-loader" /> : props.children}
        </AuthContext.Provider>
    );

};

export default AuthContext;
